import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, Building2, Loader2, Mail, MessageCircle, RotateCw } from 'lucide-react';
import { toast } from 'sonner';

import { api, ErroApi } from '@/lib/api';
import { formatarDataHora } from '@/lib/utils';
import { mascararTelefone } from '@/lib/mascaras';
import { INTERVALO_ATUALIZACAO_MS } from '@/lib/consultas';
import { useAuth } from '@/features/auth/contexto';
import type {
  CanalNotificacao,
  Notificacao,
  SituacaoSmtp,
  StatusNotificacao,
} from '@/features/notificacoes/tipos';
import { Botao } from '@/components/ui/button';
import { Etiqueta } from '@/components/ui/badge';

const CANAIS: Record<
  CanalNotificacao,
  { rotulo: string; icone: React.ComponentType<{ className?: string; 'aria-hidden'?: boolean }> }
> = {
  EMAIL_PACIENTE: { rotulo: 'E-mail ao paciente', icone: Mail },
  EMAIL_UBS: { rotulo: 'Cópia à UBS', icone: Building2 },
  WHATSAPP_N8N: { rotulo: 'WhatsApp', icone: MessageCircle },
};

const STATUS: Record<
  StatusNotificacao,
  { rotulo: string; tom: 'neutro' | 'sucesso' | 'alerta' | 'perigo' }
> = {
  PENDENTE: { rotulo: 'na fila', tom: 'alerta' },
  ENVIADO: { rotulo: 'enviada', tom: 'sucesso' },
  FALHOU: { rotulo: 'falhou', tom: 'perigo' },
  REPROCESSADO: { rotulo: 'reenviada', tom: 'sucesso' },
};

/** O destino do WhatsApp é gravado só com dígitos, como o n8n espera. */
function destinoLegivel(notificacao: Notificacao): string {
  return notificacao.canal === 'WHATSAPP_N8N'
    ? mascararTelefone(notificacao.destino)
    : notificacao.destino;
}

interface PainelNotificacoesProps {
  solicitacaoId: string;
  /** Sem e-mail do paciente não existe aviso a criar — e isso precisa ser dito. */
  pacienteEmail: string | null;
  agendada: boolean;
}

/**
 * Avisos disparados pelo agendamento.
 *
 * A UBS também vê a lista: quando o paciente liga dizendo que não recebeu
 * nada, quem atende precisa saber se o aviso saiu, para onde e quando — sem
 * depender de pedir isso à Regulação. Só o reenvio é restrito.
 */
export function PainelNotificacoes({
  solicitacaoId,
  pacienteEmail,
  agendada,
}: PainelNotificacoesProps) {
  const { usuario } = useAuth();
  const queryClient = useQueryClient();

  const podeReenviar = usuario?.perfil === 'REGULACAO' || usuario?.perfil === 'ADMINISTRADOR';

  // Quem reenvia precisa saber por onde a mensagem sai: "reenviado" com o
  // servidor de teste no meio é exatamente o relato de "não chegou".
  const { data: transporte } = useQuery({
    queryKey: ['smtp-situacao'],
    queryFn: () => api.get<SituacaoSmtp>('/smtp/situacao'),
    enabled: podeReenviar,
    staleTime: 5 * 60_000,
    retry: false,
  });

  const { data: notificacoes, isPending } = useQuery({
    queryKey: ['notificacoes', solicitacaoId],
    queryFn: () => api.get<Notificacao[]>(`/solicitacoes/${solicitacaoId}/notificacoes`),
    // Enquanto houver aviso na fila, a lista sozinha muda de estado.
    refetchInterval: INTERVALO_ATUALIZACAO_MS,
  });

  const reenviar = useMutation({
    mutationFn: (id: string) => api.post(`/notificacoes/${id}/reenviar`, {}),
    onSuccess: async () => {
      toast.success('Reenvio na fila.');
      await queryClient.invalidateQueries({ queryKey: ['notificacoes', solicitacaoId] });
      await queryClient.invalidateQueries({ queryKey: ['historico', solicitacaoId] });
    },
    onError: (erro) =>
      toast.error(erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível reenviar.'),
  });

  if (isPending) {
    return <p className="text-silenciado-texto text-sm">Carregando avisos…</p>;
  }

  // A ausência do aviso ao paciente é silenciosa por natureza: sem e-mail
  // cadastrado, nada é criado. Quem atende o telefone precisa saber disso
  // antes de concluir que o sistema falhou.
  const semEmailDoPaciente = agendada && !pacienteEmail;

  const avisoTransporte = transporte?.configuracaoIgnorada ? (
    <p className="border-status-devolvido-borda bg-status-devolvido-fundo text-status-devolvido-texto flex items-start gap-2 rounded-md border p-3 text-xs">
      <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
      <span>
        Os e-mails estão saindo pelo servidor do ambiente ({transporte.host}), e não pelo servidor
        institucional configurado. Um envio marcado como concluído aqui não chegou ao destinatário —
        avise a administração para ativar a conexão de e-mail.
      </span>
    </p>
  ) : null;

  const avisoSemEmail = semEmailDoPaciente ? (
    <p className="border-status-pendencia-borda bg-status-pendencia-fundo text-status-pendencia-texto flex items-start gap-2 rounded-md border p-3 text-xs">
      <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
      <span>
        O paciente não tem e-mail cadastrado, então o aviso por e-mail não chegou a ser criado. A
        cópia à UBS e o WhatsApp, quando configurado, seguem valendo.
      </span>
    </p>
  ) : null;

  if (!notificacoes?.length) {
    return (
      <div className="flex flex-col gap-3">
        {avisoTransporte}
        {avisoSemEmail}
        <p className="text-silenciado-texto text-sm">
          Nenhum aviso enviado. Os avisos são disparados quando a solicitação é agendada.
        </p>
      </div>
    );
  }

  return (
    <ul className="flex flex-col gap-3">
      {avisoTransporte && <li>{avisoTransporte}</li>}
      {avisoSemEmail && <li>{avisoSemEmail}</li>}
      {notificacoes.map((notificacao) => {
        const canal = CANAIS[notificacao.canal];
        const status = STATUS[notificacao.status];
        const Icone = canal.icone;
        const emAndamento = reenviar.isPending && reenviar.variables === notificacao.id;

        return (
          <li key={notificacao.id} className="border-borda rounded-md border p-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="flex items-center gap-1.5 text-sm font-medium">
                  <Icone className="size-4 shrink-0" aria-hidden={true} />
                  {canal.rotulo}
                </p>
                <p
                  className="text-silenciado-texto mt-0.5 truncate text-xs"
                  title={notificacao.destino}
                >
                  {destinoLegivel(notificacao)}
                </p>
              </div>

              <Etiqueta tom={status.tom}>{status.rotulo}</Etiqueta>
            </div>

            <p className="text-silenciado-texto mt-2 text-xs">
              {notificacao.enviadoEm
                ? `Enviado em ${formatarDataHora(notificacao.enviadoEm)}`
                : `Criado em ${formatarDataHora(notificacao.criadoEm)}`}
              {notificacao.tentativas > 1 && ` · ${notificacao.tentativas} tentativas`}
            </p>

            {notificacao.ultimoErro && (
              <p className="text-status-devolvido-texto mt-2 flex items-start gap-1.5 text-xs">
                <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                <span className="min-w-0 break-words">{notificacao.ultimoErro}</span>
              </p>
            )}

            {podeReenviar && (
              <Botao
                type="button"
                variante="contorno"
                tamanho="sm"
                className="mt-3"
                onClick={() => reenviar.mutate(notificacao.id)}
                disabled={reenviar.isPending}
              >
                {emAndamento ? (
                  <Loader2 className="animate-spin" aria-hidden="true" />
                ) : (
                  <RotateCw aria-hidden="true" />
                )}
                Reenviar
              </Botao>
            )}
          </li>
        );
      })}
    </ul>
  );
}
